import {
  FilterDefinition,
  FilterState,
  FilterStateApi,
  FilterType,
  FilterValues,
  FiltersValues,
  BaseFilterValues,
  DateFilterValue
} from '../../../../types'
import { FILTER_TYPES } from '../constants'
import { stateApi as attributeStateApi } from '../types/attribute'
import { stateApi as keyValueStateApi } from '../types/key-value'
import { stateApi as selectionStateApi } from '../types/selection'
import { stateApi as dateStateApi } from '../types/date'

const singleStateApi = {
  getFilterCount: (filters: FiltersValues, filterKey: string) => (filters[filterKey] ? 1 : 0),
  updateFilter: ({ filterKey, id }: FilterState) => filterKey && { [filterKey]: id }
}

const filterTypeApiMap = {
  [FILTER_TYPES.ATTRIBUTE]: attributeStateApi,
  [FILTER_TYPES.SINGLE]: singleStateApi,
  [FILTER_TYPES.SELECTION]: selectionStateApi,
  [FILTER_TYPES.KEYVALUE]: keyValueStateApi,
  [FILTER_TYPES.DATE]: dateStateApi
} as Record<FilterType, FilterStateApi>

export const isFilterSet = (filter: FilterValues) =>
  (filter as BaseFilterValues)?.length || (filter as DateFilterValue)?.startDate

export const updateFilterState = (
  filterDef: FilterDefinition,
  {
    filters,
    filterKey,
    filterState
  }: { filters?: FiltersValues; filterKey: string; filterState: FilterState }
) => {
  const { filterType } = filterDef
  const updateFn = filterTypeApiMap[filterType]?.updateFilter

  if (!updateFn) {
    console.error(
      `useFilters.updateFilter: updateFn not found for ${filterKey}, type:${filterType}`,
      filterDef,
      { filters, filterKey, filterState }
    )

    return
  }

  return updateFn({ filters, filterKey, ...filterState })
}

export const getFilterCount = ({
  filters,
  filterKey,
  filterType
}: {
  filters: FiltersValues
  filterKey: string
  filterType: FilterType
}) => {
  const { getFilterCount } = filterTypeApiMap[filterType]

  // @ts-ignore
  return getFilterCount ? getFilterCount(filters, filterKey) : 0
}

export const getFilterCountTotal = ({
  filterDefs = [],
  filters = {}
}: {
  filterDefs?: FilterDefinition[]
  filters?: FiltersValues
} = {}) =>
  // prettier-ignore
  Object
    .keys(filters)
    .reduce((acc, filterKey) => {
      // @ts-ignore both these keys exist on FilterDefinition but ts doesn't think so
      const { filterType, alwaysSet } = filterDefs
        .find((it: FilterDefinition) => it.filterKey === filterKey)

      // to change this to applied filter types simply getFilterCount() > 0 ? 1 : 0
      // I feel this may be request in the future, though I checked this with design
      return acc +
        (alwaysSet ? 0 : getFilterCount({ filters, filterKey, filterType }))
  }, 0)

// utils for reading and writing url parameters

/** We would like to replace this with React Router useSearchParams when we can */
// looks for standard and alternative keys to obtain url parameters
const getFinalParams = (filterDef: FilterDefinition, urlParams: URLSearchParams) => {
  const { filterKey, altFilterKey } = filterDef
  const paramsInitial = urlParams?.getAll(filterKey)
  const paramsFinal = paramsInitial?.length ? paramsInitial : urlParams.getAll(altFilterKey || '')

  return paramsFinal?.length ? paramsFinal.map(decodeURI) : undefined
}

export const readUrlParams = (filterDef: FilterDefinition, urlParams: URLSearchParams) => {
  const { filterType } = filterDef
  const { readUrl } = filterTypeApiMap[filterType]

  if (readUrl) {
    return readUrl(filterDef, urlParams)
  } else {
    const params = getFinalParams(filterDef, urlParams)

    return params?.length && filterType === FILTER_TYPES.SINGLE ? params[0] : params
  }
}

/**
 * Removes query parameters that are managed by the filter state
 * @param params current URLSearchParams
 * @param filterDefs filter definitions
 * @returns URLSearchParams only containing unmanaged query parameters
 */
const clearManagedParams = (params: URLSearchParams, filterDefs: FilterDefinition[]) => {
  const unmanagedParams = new URLSearchParams(params) // clone to new object

  filterDefs.forEach(({ filterKey, altFilterKey }) => {
    params.forEach((value, key) => {
      // include alt key allow altFilterKey will be replaced by its filterKey after load
      if (key.startsWith(filterKey) || (altFilterKey && key.startsWith(altFilterKey))) {
        unmanagedParams.delete(key)
      }
    })
  })

  return unmanagedParams
}

// write utils
export const updateUrlParams = (
  currentParams: URLSearchParams,
  filterDefs: FilterDefinition[],
  filters: FiltersValues
) => {
  const updatedUrlSearchParams = clearManagedParams(currentParams, filterDefs)

  for (const filterName in filters) {
    if (isFilterSet(filters[filterName])) {
      const { filterType } = filterDefs.find((it) => it.filterKey === filterName) ?? {
        filterType: 'selection'
      }
      const { updateUrl } = filterTypeApiMap[filterType]

      if (Array.isArray(filters[filterName])) {
        if (updateUrl) {
          updateUrl(filters, filterName, updatedUrlSearchParams)
        } else {
          // @ts-ignore we know this is an array due to line 96 above
          filters[filterName].forEach((value) =>
            updatedUrlSearchParams.append(filterName, encodeURI(value))
          )
        }
      } else {
        if (updateUrl) {
          updateUrl(filters, filterName, updatedUrlSearchParams)
        } else {
          updatedUrlSearchParams.set(filterName, encodeURI(filters[filterName] as string))
        }
      }
    } else {
      updatedUrlSearchParams.delete(filterName)
    }
  }

  return updatedUrlSearchParams
}
